import React, { useId, useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { Checkbox, Chip, CircularProgress } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useQuery } from '@tanstack/react-query';
import { getAppCategories } from 'api/appManager';
import { deepClone } from '@tenant/utility/helper/Utils';
import PropTypes from 'prop-types';

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

const FIELD_NAME = 'categoryList';
const KEY = 'Id';
const LABEL = 'Name';

const CategorySelect = (props) => {
  const {
    error,
    helperText,
    value = [],
    required,
    excludes,
    textFieldProps = {},
    isMultiple = true,
    ...rest
  } = props;
  const [enabled, setEnabled] = useState(false);
  const id = useId();
  const { data: categories, isLoading } = useQuery({
    enabled,
    queryKey: ['list-categories-select'],
    queryFn: async () => {
      return await getAppCategories();
    },
  });
  const options = useMemo(() => {
    let result = deepClone(
      categories?.filter((e) => !excludes?.some((f) => f[KEY] === e[KEY])) ??
        [],
    );

    if (value) {
      result = Array.isArray(value)
        ? [...value, ...result]
        : [value, ...result];
    }

    // Filter out duplicates based on the Id field
    const uniqueMap = new Map();

    result.forEach((item) => {
      if (item?.[KEY] && !uniqueMap.has(item[KEY])) {
        uniqueMap.set(item[KEY], item);
      }
    });

    return Array.from(uniqueMap.values());
  }, [categories]);

  const onOpen = () => {
    setEnabled(true);
  };

  return (
    <Autocomplete
      fullWidth
      multiple={isMultiple}
      disableCloseOnSelect={isMultiple}
      value={value}
      id={`${FIELD_NAME}-select-${id}`}
      name={FIELD_NAME}
      options={options ?? []}
      getOptionLabel={(option) => option[LABEL]}
      isOptionEqualToValue={(option, value) => {
        return option?.[KEY] === value?.[KEY];
      }}
      renderOption={(props, option, { selected }) => {
        const { ...optionProps } = props ?? {};
        return (
          <li {...optionProps} key={option?.[KEY]}>
            {isMultiple && (
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
            )}
            {option[LABEL]}
          </li>
        );
      }}
      loading={isLoading}
      renderInput={({ ...params }) => (
        <TextField
          {...params}
          required={required}
          error={error}
          helperText={helperText}
          placeholder={isMultiple ? 'Select Categories' : 'Select Category'}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isLoading ? (
                  <CircularProgress color='inherit' size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
          {...textFieldProps}
        />
      )}
      renderTags={(tagValue, getTagProps) => {
        return tagValue.map((option, index) => (
          <Chip
            label={option[LABEL]}
            {...getTagProps({ index })}
            key={option[KEY]}
          />
        ));
      }}
      onOpen={onOpen}
      {...rest}
    />
  );
};

export default React.memo(CategorySelect);

CategorySelect.propTypes = {
  error: PropTypes.bool,
  helperText: PropTypes.string,
  value: PropTypes.array,
  required: PropTypes.bool,
  excludes: PropTypes.array,
  textFieldProps: PropTypes.object,
  isMultiple: PropTypes.bool,
};
